/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { VerboseMsgType } from '@adp-common/chat-core';

import {
  getVerboseContentObj,
  isStreamPluginFinish,
  isVerboseContent,
  isKnowledgeRecallVerboseContentDeprecated,
} from '../verbose';
import { safeJSONParse } from '../safe-json-parse';
import { primitiveExhaustiveCheck } from '../exhaustive-check';
import type { Message } from '../../store/types';
import {
  type FunctionCallMessageUnit,
  type MessageExt,
  MessageUnitRole,
} from './types';

export const getMessageTimeCost = (ext?: MessageExt) => ext?.time_cost;

export const getMessageUnitLLMTimeCost = (
  messageUnit: FunctionCallMessageUnit,
) =>
  messageUnit.llmTime ?? getMessageTimeCost(messageUnit.llmOutput.extra_info);

export const getMessageUnitToolTimeCost = (
  messageUnit: FunctionCallMessageUnit,
) =>
  messageUnit.toolTime ??
  getMessageTimeCost(messageUnit.apiResponse?.extra_info);

const parseTimeCost = (timeCost?: string) => {
  if (!timeCost?.trim()) {
    return undefined;
  }
  const parsedTimeCost = Number(timeCost);
  return Number.isFinite(parsedTimeCost) && parsedTimeCost >= 0
    ? parsedTimeCost
    : undefined;
};

export const formatMessageTimeCost = (timeCost?: string) => {
  const value = parseTimeCost(timeCost);
  return value === undefined ? '—' : `${value.toFixed(1)}s`;
};

export const getFinalAnswerTimeCost = (messages: Message[]) => {
  const times = messages
    .filter(message => message.is_finish && message.type === 'answer')
    .map(message => parseTimeCost(message.extra_info.time_cost))
    .filter((time): time is number => time !== undefined);
  return times.length ? Math.max(...times).toFixed(1) : undefined;
};

const getFunctionCallIDFromContent = (content?: string) => {
  const parsedContent = safeJSONParse(content ?? '');
  if (
    parsedContent &&
    typeof parsedContent === 'object' &&
    !Array.isArray(parsedContent) &&
    'id' in parsedContent &&
    typeof parsedContent.id === 'string'
  ) {
    return parsedContent.id;
  }
  return undefined;
};

const getMessageCallID = (message: Message) =>
  message.extra_info.call_id || getFunctionCallIDFromContent(message.content);

const formatTimeCost = (timeCost?: number) => timeCost?.toFixed(1) ?? '';

const getDurationFromCheckpoint = (
  checkpoint: number | undefined,
  previousCheckpoint: number | undefined,
) =>
  checkpoint === undefined || previousCheckpoint === undefined
    ? undefined
    : Math.max(checkpoint - previousCheckpoint, 0);

// Backend process message time_cost values are cumulative run checkpoints.
// The running-process UI needs per-step durations, so convert them to deltas.
const normalizeCumulativeTimeCosts = (
  messageUnits: FunctionCallMessageUnit[],
) => {
  let previousCheckpoint: number | undefined = 0;

  return messageUnits.map(messageUnit => {
    if (messageUnit.role === MessageUnitRole.TOOL) {
      const llmCheckpoint = parseTimeCost(
        getMessageTimeCost(messageUnit.llmOutput.extra_info),
      );
      const toolCheckpoint = parseTimeCost(
        getMessageTimeCost(messageUnit.apiResponse?.extra_info),
      );
      const llmTime = getDurationFromCheckpoint(
        llmCheckpoint,
        previousCheckpoint,
      );
      const toolTime = messageUnit.apiResponse
        ? getDurationFromCheckpoint(toolCheckpoint, llmCheckpoint)
        : 0;
      const totalTime =
        llmTime === undefined || toolTime === undefined
          ? undefined
          : llmTime + toolTime;

      const checkpoint = messageUnit.apiResponse
        ? toolCheckpoint
        : llmCheckpoint;
      previousCheckpoint =
        checkpoint === undefined
          ? undefined
          : Math.max(previousCheckpoint ?? 0, checkpoint);

      return {
        ...messageUnit,
        time: formatTimeCost(totalTime),
        llmTime: formatTimeCost(llmTime),
        toolTime: messageUnit.apiResponse
          ? formatTimeCost(toolTime)
          : undefined,
      };
    }

    if (
      [MessageUnitRole.DATA_SET, MessageUnitRole.VERBOSE].includes(
        messageUnit.role,
      )
    ) {
      const checkpoint = parseTimeCost(messageUnit.time);
      const time = getDurationFromCheckpoint(checkpoint, previousCheckpoint);
      previousCheckpoint =
        checkpoint === undefined
          ? undefined
          : Math.max(previousCheckpoint ?? 0, checkpoint);

      return {
        ...messageUnit,
        time: formatTimeCost(time),
      };
    }

    return messageUnit;
  });
};

export const getMessageUnitsByFunctionCallMessageList = (
  functionCallMessageList: Message[],
) => {
  // Generate messageUnits
  const messageUnits = functionCallMessageList
    .map((m, i) => {
      const role = getRoleByMessage(m);
      if (!role) {
        return null;
      }
      return {
        role,
        llmOutput: m,
        // Follow-up unified ID pair matching
        callId: getMessageCallID(m),
        // TODO: [This method is to cover the bottom, and then gradually drop it, all use id matching] The message order is reversed, and the response message is in the first place.
        apiIndexMark: role === MessageUnitRole.TOOL ? i - 1 : undefined,
        time: getMessageTimeCost(m.extra_info),
      };
    })
    .filter(Boolean) as FunctionCallMessageUnit[];

  // Modify messageUnits according to tool_response
  const modifiedMessageUnits = functionCallMessageList.reduceRight(
    (acc, message, index) => {
      modifyMessageUnitByToolResponseMessage(message, acc, index);
      return acc;
    },
    messageUnits,
  );

  // The received message is a flashback and needs to be rendered in order.
  return normalizeCumulativeTimeCosts(modifiedMessageUnits.reverse());
};

//Get the corresponding role according to the message
const getRoleByMessage = (message: Message) => {
  const { type, content } = message;

  if (type === 'knowledge') {
    return MessageUnitRole.DATA_SET;
  }
  if (type === 'function_call') {
    return MessageUnitRole.TOOL;
  }

  if (type !== 'verbose') {
    return;
  }

  const parsedContent = safeJSONParse(content);

  if (!isVerboseContent(parsedContent)) {
    /**
     * This is the legacy error verbose data, temporarily retained to make the online function normal
     */
    if (isKnowledgeRecallVerboseContentDeprecated(parsedContent)) {
      return MessageUnitRole.DATA_SET;
    }
    return;
  }

  const { msg_type } = parsedContent;
  if (msg_type === VerboseMsgType.HOOK_CALL) {
    return MessageUnitRole.HOOKS;
  }
  if (msg_type === VerboseMsgType.KNOWLEDGE_RECALL) {
    return MessageUnitRole.DATA_SET;
  }

  /** Verbose protocol to render */
  if (
    msg_type === VerboseMsgType.JUMP_TO ||
    msg_type === VerboseMsgType.BACK_WORD ||
    msg_type === VerboseMsgType.LONG_TERM_MEMORY
  ) {
    return MessageUnitRole.VERBOSE;
  }

  /**
   * These records are lifecycle metadata or rendered separately.
   */
  if (
    msg_type === VerboseMsgType.GENERATE_ANSWER_FINISH ||
    msg_type === VerboseMsgType.STREAM_PLUGIN_FINISH ||
    msg_type === VerboseMsgType.INTERRUPT ||
    msg_type === VerboseMsgType.HARNESS_CONTEXT
  ) {
    return;
  }

  primitiveExhaustiveCheck(msg_type);

  return;
};

// Index + 1, streamId bottom matching function_call, compatible scenes: ordinary scenes, streaming plug-ins
const findTargetToolUnit = (
  messageUnits: FunctionCallMessageUnit[],
  index: number,
  streamUuid?: string,
) =>
  messageUnits.find<FunctionCallMessageUnit>(
    (unit): unit is FunctionCallMessageUnit =>
      unit.role === MessageUnitRole.TOOL &&
      (streamUuid
        ? unit.streamUuid === streamUuid
        : unit.apiIndexMark === index),
  );
// function_call universal matching mechanism, id pair matching
const findTargetFunctionCall = (
  messageUnits: FunctionCallMessageUnit[],
  id?: string,
) =>
  messageUnits.find<FunctionCallMessageUnit>(
    (unit): unit is FunctionCallMessageUnit =>
      unit.role === MessageUnitRole.TOOL && unit.callId === id,
  );

// Match function_call id, match the id in the tool_response with it, and update the plugin results
const handelMatchCallId = (
  message: Message,
  messageUnits: FunctionCallMessageUnit[],
) => {
  const targetToolUnit = findTargetFunctionCall(
    messageUnits,
    message.extra_info.call_id,
  );
  if (!targetToolUnit) {
    return;
  }
  targetToolUnit.apiResponse = message;
  targetToolUnit.isFinish = true;
  targetToolUnit.time = (
    Number(targetToolUnit.time ?? '0') +
    Number(getMessageTimeCost(message.extra_info) ?? '0')
  ).toFixed(1);
  return;
};

/** Process tool_response result, find the corresponding function call, and insert it into the output
 *  Scenario: ID pair matching, index + 1 normal matching, streaming plug-in verbose matching
 */
const modifyMessageUnitByToolResponseMessage = (
  message: Message,
  messageUnits: FunctionCallMessageUnit[],
  index: number,
) => {
  // Plugin related news
  // P0, priority matching function call_id
  if (isHaveFunctionId(message)) {
    handelMatchCallId(message, messageUnits);
    return;
  }
  // 1. Non-streaming plugins
  if (isNormalPlugin(message)) {
    // Get target ToolUnit
    const targetToolUnit = findTargetToolUnit(messageUnits, index);
    if (!targetToolUnit) {
      return;
    }
    targetToolUnit.apiResponse = message;
    targetToolUnit.isFinish = true;
    targetToolUnit.time = (
      Number(targetToolUnit.time ?? '0') +
      Number(getMessageTimeCost(message.extra_info) ?? '0')
    ).toFixed(1);
    return;
  }
  // 2. Start streaming plugins
  if (isStreamPluginRunning(message)) {
    // Get target ToolUnit
    const targetToolUnit = findTargetToolUnit(messageUnits, index);
    if (!targetToolUnit) {
      return;
    }
    targetToolUnit.streamUuid = message.extra_info.stream_plugin_running;
    targetToolUnit.apiResponse = message;
    return;
  }
  // 2. End of streaming plugin
  if (isStreamPluginFinish(message)) {
    const messageContentObj =
      getVerboseContentObj<VerboseMsgType.STREAM_PLUGIN_FINISH>(
        message.content,
      );
    if (!messageContentObj) {
      return;
    }
    const { dataObj } = messageContentObj;
    if (!dataObj) {
      return;
    }
    const { tool_output_content, uuid } = dataObj;
    // Get target ToolUnit
    const targetToolUnit = findTargetToolUnit(messageUnits, index, uuid);
    if (!targetToolUnit) {
      return;
    }
    targetToolUnit.isFinish = true;
    targetToolUnit.apiResponse = {
      ...message,
      content: tool_output_content,
    };
    targetToolUnit.time = (
      Number(targetToolUnit.time ?? '0') +
      Number(getMessageTimeCost(message.extra_info) ?? '0')
    ).toFixed(1);
  }
};

/**
 * A grouped representation of one or more {@link FunctionCallMessageUnit}s
 * produced by the LLM in a single ReAct step. When the backend dispatches
 * multiple parallel tool calls, they share a `tool_call_group_id` on
 * `extra_info` and should be presented to the user as one row instead of
 * several. Single tool calls (and non-tool entries such as dataset / verbose
 * units) remain as one-unit groups so existing rendering behavior is
 * preserved.
 */
export interface FunctionCallMessageUnitGroup {
  kind: 'single_tool' | 'parallel_tools';
  /** Stable id; prefers `tool_call_group_id`, then call id, then message id */
  id: string;
  /** Tool name from the first unit (may be empty for non-tool units) */
  toolName?: string;
  /** Number of underlying message units in this group */
  callCount: number;
  units: FunctionCallMessageUnit[];
  /** LLM step time for the group (representative value, not a sum) */
  llmTime: string;
  /** Tool response wall-clock time for the group, if any unit had a response */
  toolTime?: string;
  /** Wall-clock total time for the group (representative value, not a sum) */
  totalTime: string;
}

// `tool_call_group_id` is appended by the backend on parallel function-call
// rows; the frontend `MessageExt` type does not enumerate it yet, so we read
// it via a structural cast to avoid coupling to upstream type changes.
const getToolCallGroupId = (
  unit: FunctionCallMessageUnit,
): string | undefined => {
  const ext = unit.llmOutput.extra_info as
    | (MessageExt & { tool_call_group_id?: unknown })
    | undefined;
  const value = ext?.tool_call_group_id;
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

const getToolName = (unit: FunctionCallMessageUnit): string | undefined => {
  const name = unit.llmOutput.extra_info?.tool_name;
  return name ? name : undefined;
};

const getRepresentativeTime = (
  units: FunctionCallMessageUnit[],
  pick: (unit: FunctionCallMessageUnit) => string | undefined,
) => {
  // Parallel units share the same backend checkpoint, so after cumulative
  // normalization only one of them carries the non-zero delta. Picking the
  // max yields the wall-clock duration whether the input was already
  // normalized (one non-zero, rest zero) or carries the same value on every
  // unit (all equal). It deliberately does NOT sum child durations.
  let max = 0;
  for (const unit of units) {
    const value = parseTimeCost(pick(unit));
    if (value === undefined) {
      return undefined;
    }
    if (value > max) {
      max = value;
    }
  }
  return max;
};

/**
 * Group a sequence of {@link FunctionCallMessageUnit}s into render-ready
 * groups. Adjacent tool units that share a non-empty `tool_call_group_id` on
 * `extra_info` are merged into a single `parallel_tools` group. Tool units
 * without a group id, single tool calls, and non-tool units remain as
 * standalone `single_tool` groups so existing UI behavior is preserved.
 */
export const getFunctionCallMessageUnitGroups = (
  messageUnits: FunctionCallMessageUnit[],
): FunctionCallMessageUnitGroup[] => {
  const groups: FunctionCallMessageUnitGroup[] = [];
  let pendingGroupId: string | undefined;
  let pending: FunctionCallMessageUnit[] = [];

  const flush = () => {
    const first = pending[0];
    if (!first) {
      return;
    }
    const units = pending;
    const fallbackId =
      first.callId ?? first.llmOutput.message_id ?? `group-${groups.length}`;
    const id = pendingGroupId ?? fallbackId;
    if (units.length > 1) {
      const toolHasAny = units.some(unit => unit.toolTime !== undefined);
      const llmTime = getRepresentativeTime(units, u => u.llmTime);
      const toolTime = toolHasAny
        ? getRepresentativeTime(units, u => u.toolTime)
        : undefined;
      groups.push({
        kind: 'parallel_tools',
        id,
        toolName: getToolName(first),
        callCount: units.length,
        units,
        llmTime: formatTimeCost(llmTime),
        toolTime: toolHasAny ? formatTimeCost(toolTime) : undefined,
        // The shared model duration and slowest tool can belong to different
        // children after normalization, so max(child.total) undercounts.
        totalTime: formatTimeCost(
          llmTime === undefined || toolTime === undefined
            ? undefined
            : llmTime + toolTime,
        ),
      });
    } else {
      groups.push({
        kind: 'single_tool',
        id,
        toolName: getToolName(first),
        callCount: 1,
        units,
        llmTime: first.llmTime ?? first.time ?? '',
        toolTime: first.toolTime,
        totalTime: first.time ?? first.llmTime ?? '',
      });
    }
    pending = [];
    pendingGroupId = undefined;
  };

  for (const unit of messageUnits) {
    if (unit.role !== MessageUnitRole.TOOL) {
      // Never group non-tool units (dataset / verbose / hooks) with tool
      // units. Emit each as its own standalone group to preserve existing
      // rendering.
      flush();
      groups.push({
        kind: 'single_tool',
        id: unit.llmOutput.message_id ?? `group-${groups.length}`,
        toolName: undefined,
        callCount: 1,
        units: [unit],
        llmTime: unit.llmTime ?? unit.time ?? '',
        toolTime: unit.toolTime,
        totalTime: unit.time ?? '',
      });
      continue;
    }
    const groupId = getToolCallGroupId(unit);
    // Only continue an existing group when both ids are defined and equal —
    // never collapse `undefined === undefined` into a parallel group, which
    // would falsely merge legacy single tool calls.
    if (groupId !== undefined && groupId === pendingGroupId) {
      pending.push(unit);
    } else {
      flush();
      pendingGroupId = groupId;
      pending = [unit];
    }
  }
  flush();
  return groups;
};

// Is it a streaming plugin?
export function isStreamPlugin(message: Message): boolean {
  return isStreamPluginRunning(message) || isStreamPluginFinish(message);
}

export function isStreamPluginRunning(message: Message): boolean {
  return (
    message.type === 'tool_response' &&
    !!message.extra_info.stream_plugin_running
  );
}

export function isNormalPlugin(message: Message): boolean {
  return message.type === 'tool_response' && !isStreamPlugin(message);
}

// Is there a function_call ID match?
export function isHaveFunctionId(message: Message): boolean {
  return message.type === 'tool_response' && !!message.extra_info.call_id;
}
